// Production action orchestration helper for AI credit-billing flows.
// Extracted minimally from ScanTab/App so tests can import the REAL logic
// instead of toy copies. This module has no supabase dependency — it only
// depends on the credit-cost map and recipe validation, which are pure.

import { getCreditsForEndpoint, checkCreditsForAction as checkCreditsPure, type CreditCheckResult } from "./creditCosts";
import { validateRecipe } from "./recipeValidation";
import type { AiRecipe } from "../types";

export interface CanonicalNutrition {
  calories?: number | null;
  protein?: number | null;
  fat?: number | null;
  carbs?: number | null;
  sugar?: number | null;
  fiber?: number | null;
  sodium?: number | null;
}

export type HealthScoreLike = { [K in keyof Required<CanonicalNutrition>]: number | null };

export interface MockTransport {
  fetch(endpoint: string, body: Record<string, unknown>): Promise<{ ok: boolean; status: number; json(): Promise<Record<string, unknown>> }>;
}

export interface MockRefresh {
  (): void | Promise<void>;
}

export type CheckCreditsFn = (creditsNeeded: number | null) => Promise<CreditCheckResult> | CreditCheckResult;

export interface ActionResult<T> {
  success: boolean;
  data: T | null;
  creditCheck: CreditCheckResult | null;
  refreshed: boolean;
  refreshFailed: boolean;
  error: string | null;
}

// Zero is a measured value; an explicit unknown must never become an AI estimate.
export function applyCanonicalNutrition<T extends HealthScoreLike>(result: T, known: CanonicalNutrition | undefined): void {
  if (!known) return;
  const fields = ["calories", "protein", "fat", "carbs", "sugar", "fiber", "sodium"] as const;
  for (const key of fields) {
    if (!Object.prototype.hasOwnProperty.call(known, key)) continue;
    const val = known[key];
    result[key] = typeof val === "number" && Number.isFinite(val) && val >= 0 ? val : null;
  }
  // Provider daily percentages use a different serving basis and are now stale.
  if ("dailyValues" in result) result.dailyValues = undefined;
}

// Orchestrate a credit-billed AI action end-to-end:
// 1. Check credits (fail-closed for unknown endpoints)
// 2. Call the transport (mocked in tests, real fetch in production)
// 3. Handle 402 (insufficient credits) without charging
// 4. Refresh credits in finally (success/error/timeout), guarding refresh failure
// 5. Return structured result so callers can update UI
export async function orchestrateAiAction<T extends Record<string, unknown>>(
  endpoint: string,
  body: Record<string, unknown>,
  transport: MockTransport,
  refresh: MockRefresh,
  checkCredits: CheckCreditsFn = (n) => checkCreditsPure(n, null),
): Promise<ActionResult<T>> {
  const cost = getCreditsForEndpoint(endpoint);
  const creditCheck = await checkCredits(cost);
  if (!creditCheck.allowed) {
    return { success: false, data: null, creditCheck, refreshed: false, refreshFailed: false, error: creditCheck.reason };
  }

  let refreshed = false;
  let refreshFailed = false;
  let success = false;
  let data: T | null = null;
  let error: string | null = null;

  try {
    const res = await transport.fetch(endpoint, body);
    if (res.status === 402) {
      error = "insufficient_credits";
    } else if (!res.ok) {
      error = "server_error";
    } else {
      data = await res.json() as T;
      success = true;
    }
  } catch (err) {
    error = err instanceof Error ? err.message : "network_error";
  } finally {
    try {
      await refresh();
      refreshed = true;
    } catch {
      refreshFailed = true;
      // refresh failure must not mask primary result
    }
  }

  return { success, data, creditCheck, refreshed, refreshFailed, error };
}

// Validate a recipe from an AI response using production validation.
export function validateAiRecipe(raw: unknown): { valid: boolean; recipe: AiRecipe | null; errors: string[] } {
  return validateRecipe(raw);
}
